import { NO_ARGS, SINGLE_NUMERIC, twoNumeric } from './_argShapes';
import { STUB_NOT_IMPLEMENTED } from './_stubs';
import type { EffectEntry } from './types';

// All ConditionalKind entries share `category: 'conditional'`. The
// `family` collapses every conditional to `'structural'` for system-
// prompt grouping; if we later want sub-grouping (probability vs
// cycle-counted vs spatial), split the family enum.
//
// Schema covers `condArgs` only — the conditional body (an Effect[])
// is a separate AST field validated elsewhere.

export const every: EffectEntry = {
  kind: 'every',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body every Nth cycle: every(4, x => x.rev())',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('every', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('every', 'fromAst'),
};

export const when: EffectEntry = {
  kind: 'when',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body when predicate is true',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('when', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('when', 'fromAst'),
};

export const whenmod: EffectEntry = {
  kind: 'whenmod',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body on cycles where i % m === n: whenmod(4, 3, fn)',
  args: twoNumeric(
    { name: 'mod', doc: 'modulus' },
    { name: 'remainder', doc: 'cycle index remainder to match' },
  ),
  toCode: () => STUB_NOT_IMPLEMENTED('whenmod', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('whenmod', 'fromAst'),
};

export const firstOf: EffectEntry = {
  kind: 'firstOf',
  category: 'conditional',
  family: 'structural',
  doc: 'apply on the first N cycles of a group of N',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('firstOf', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('firstOf', 'fromAst'),
};

export const lastOf: EffectEntry = {
  kind: 'lastOf',
  category: 'conditional',
  family: 'structural',
  doc: 'apply on the last N cycles of a group of N',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('lastOf', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('lastOf', 'fromAst'),
};

export const inside: EffectEntry = {
  kind: 'inside',
  category: 'conditional',
  family: 'structural',
  doc: 'run body at N× speed for the duration',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('inside', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('inside', 'fromAst'),
};

export const outside: EffectEntry = {
  kind: 'outside',
  category: 'conditional',
  family: 'structural',
  doc: 'run body at 1/N× speed',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('outside', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('outside', 'fromAst'),
};

export const sometimes: EffectEntry = {
  kind: 'sometimes',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body with 50% probability',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('sometimes', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('sometimes', 'fromAst'),
};

export const often: EffectEntry = {
  kind: 'often',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body with 75% probability',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('often', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('often', 'fromAst'),
};

export const rarely: EffectEntry = {
  kind: 'rarely',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body with 25% probability',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('rarely', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('rarely', 'fromAst'),
};

export const almostAlways: EffectEntry = {
  kind: 'almostAlways',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body with 90% probability',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('almostAlways', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('almostAlways', 'fromAst'),
};

export const almostNever: EffectEntry = {
  kind: 'almostNever',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body with 10% probability',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('almostNever', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('almostNever', 'fromAst'),
};

export const always: EffectEntry = {
  kind: 'always',
  category: 'conditional',
  family: 'structural',
  doc: 'always apply body (100%)',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('always', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('always', 'fromAst'),
};

export const never: EffectEntry = {
  kind: 'never',
  category: 'conditional',
  family: 'structural',
  doc: 'never apply body (0%)',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('never', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('never', 'fromAst'),
};

export const someCycles: EffectEntry = {
  kind: 'someCycles',
  category: 'conditional',
  family: 'structural',
  doc: 'apply body on randomly chosen cycles',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('someCycles', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('someCycles', 'fromAst'),
};

export const someCyclesBy: EffectEntry = {
  kind: 'someCyclesBy',
  category: 'conditional',
  family: 'structural',
  doc: 'like someCycles with explicit probability',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('someCyclesBy', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('someCyclesBy', 'fromAst'),
};

export const jux: EffectEntry = {
  kind: 'jux',
  category: 'conditional',
  family: 'structural',
  doc: 'duplicate pattern panned hard L/R, body runs on R: jux(x => x.rev())',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('jux', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('jux', 'fromAst'),
};

export const juxBy: EffectEntry = {
  kind: 'juxBy',
  category: 'conditional',
  family: 'structural',
  doc: 'jux with pan width: juxBy(0.5, fn)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('juxBy', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('juxBy', 'fromAst'),
};

export const off: EffectEntry = {
  kind: 'off',
  category: 'conditional',
  family: 'structural',
  doc: 'overlay a time-shifted copy: off(0.125, x => x.fast(2))',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('off', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('off', 'fromAst'),
};

export const chunk: EffectEntry = {
  kind: 'chunk',
  category: 'conditional',
  family: 'structural',
  doc: 'cycle through N chunks, applying body to one per cycle',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('chunk', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('chunk', 'fromAst'),
};

export const superimpose: EffectEntry = {
  kind: 'superimpose',
  category: 'conditional',
  family: 'structural',
  doc: 'layer body on top of the original: superimpose(x => x.fast(2))',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('superimpose', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('superimpose', 'fromAst'),
};

export const echoWith: EffectEntry = {
  kind: 'echoWith',
  category: 'conditional',
  family: 'structural',
  doc: 'echo with a transform per repeat: echoWith(3, 0.125, fn)',
  args: twoNumeric(
    { name: 'n', doc: 'number of repeats' },
    { name: 'shift', doc: 'time shift between repeats in cycles' },
  ),
  toCode: () => STUB_NOT_IMPLEMENTED('echoWith', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('echoWith', 'fromAst'),
};
